import React from 'react';
import { DropdownButton } from '@ygtechnologies/rubus-ui';
import { faBracketsCurly } from '@fortawesome/pro-light-svg-icons';
import { EmailPreviewKind } from '@/services/emailTemplates';
import theme from './EmailTemplates.module.scss';

interface Placeholder {
    /** The spelling shown in the list. */
    label: string;
    /** What clicking inserts; defaults to the label. Snippets (each-blocks, keys to rename) differ. */
    insert?: string;
}

interface PlaceholderRow {
    placeholders: Placeholder[];
    note: React.ReactNode;
}

const ATTACHMENTS_SNIPPET = (attachments: string) =>
    `{{#each ${attachments}}}\n{{#if isImage}}\n<mj-image src="cid:{{contentId}}" width="200px" padding="8px" />\n{{/if}}\n{{/each}}`;

/**
 * What the mail pipeline actually binds (Core's HandlebarData): Env and Data are always set, User when
 * the email goes to a platform user, Project + SellerProfile when it concerns a project, Attachments
 * from the message. Content templates render against the typed object, so paths are case-insensitive —
 * except data keys, which are a per-email lookup and must match exactly.
 */
const CONTENT_ROWS: PlaceholderRow[] = [
    {
        placeholders: [{ label: '{{env.frontendUrl}}' }],
        note: 'Base URL of the web app, for building links. Always available.',
    },
    {
        placeholders: [{ label: '{{data.<key>}}', insert: '{{data.key}}' }],
        note:
            'Values supplied by the specific email being sent — every template gets its own keys, so mirror ' +
            'the ones the existing template uses. Keys must match exactly (camelCase). Always available.',
    },
    {
        placeholders: [{ label: '{{user.firstName}}' }, { label: '{{user.lastName}}' }, { label: '{{user.email}}' }],
        note: 'The recipient. Set when the email goes to a platform user.',
    },
    {
        placeholders: [
            { label: '{{project.name}}' },
            { label: '{{project.description}}' },
            { label: '{{project.headerImageFileId}}' },
        ],
        note: 'Set when the email concerns a project.',
    },
    {
        placeholders: [{ label: '{{sellerProfile.name}}' }, { label: '{{sellerProfile.imageFileId}}' }],
        note: 'The seller profile that owns the project. Set together with project.',
    },
    {
        placeholders: [{ label: '{{#each attachments}} … {{/each}}', insert: ATTACHMENTS_SNIPPET('attachments') }],
        note: (
            <>
                The message&rsquo;s inline attachments. Inside the block: <code>{'{{contentId}}'}</code> — embed with{' '}
                <code>{'<mj-image src="cid:{{contentId}}" />'}</code> — and <code>{'{{isImage}}'}</code>.
            </>
        ),
    },
];

/** Same data, but the layout renders against a lookup table — the first segment must match exactly. */
const LAYOUT_ROWS: PlaceholderRow[] = [
    {
        placeholders: [{ label: '{{{body}}}' }],
        note: 'The rendered content template. Already HTML, hence the triple braces. Every layout must place it.',
    },
    {
        placeholders: [{ label: '{{Env.frontendUrl}}' }],
        note: 'Base URL of the web app, for building links. Always available.',
    },
    {
        placeholders: [{ label: '{{Data.<key>}}', insert: '{{Data.key}}' }],
        note: 'Values supplied by the specific email being sent; keys differ per template and must match exactly.',
    },
    {
        placeholders: [{ label: '{{User.firstName}}' }, { label: '{{User.lastName}}' }, { label: '{{User.email}}' }],
        note: 'The recipient. Set when the email goes to a platform user.',
    },
    {
        placeholders: [
            { label: '{{Project.name}}' },
            { label: '{{Project.description}}' },
            { label: '{{Project.headerImageFileId}}' },
        ],
        note: 'Set when the email concerns a project.',
    },
    {
        placeholders: [{ label: '{{SellerProfile.name}}' }, { label: '{{SellerProfile.imageFileId}}' }],
        note: 'The seller profile that owns the project. Set together with project.',
    },
    {
        placeholders: [{ label: '{{#each Attachments}} … {{/each}}', insert: ATTACHMENTS_SNIPPET('Attachments') }],
        note: (
            <>
                The message&rsquo;s inline attachments, with <code>{'{{contentId}}'}</code> and{' '}
                <code>{'{{isImage}}'}</code> inside the block.
            </>
        ),
    },
];

interface PlaceholderReferenceProps {
    kind: EmailPreviewKind;
    /** Inserts a placeholder at the editor cursor. Omit (read-only templates) for a plain reference. */
    onInsert?(text: string): void;
}

/**
 * "Placeholders" popover next to the template label: the structural rules for this template kind plus
 * every placeholder the mail pipeline binds. The list mirrors Core's HandlebarData, which is the
 * contract for every email sent — template-specific values only ever arrive through the data bag.
 * Clicking a placeholder inserts it at the cursor; focus then returns to the editor, which closes the
 * popover (it dismisses on focus-out).
 */
const PlaceholderReference: React.FC<PlaceholderReferenceProps> = ({ kind, onInsert }) => (
    <DropdownButton size="xs" variant="tertiary" icon={faBracketsCurly} label="Placeholders">
        <div className={theme.placeholderPanel}>
            <section className={theme.placeholderRules}>
                <h5 className={theme.placeholderSectionLabel}>Rules</h5>
                <ul>
                    {kind === 'layout' ? (
                        <li>
                            The <strong>first segment is case-sensitive</strong>: <code>{'{{Env.frontendUrl}}'}</code>{' '}
                            resolves, <code>{'{{env.frontendUrl}}'}</code> renders empty.
                        </li>
                    ) : (
                        <>
                            <li>
                                Paths are case-insensitive, except <code>data</code> keys, which must match exactly.
                            </li>
                            <li>The subject field takes the same placeholders.</li>
                        </>
                    )}
                    <li>
                        A placeholder without a value renders as empty text — typos fail silently, so check the
                        preview.
                    </li>
                </ul>
            </section>

            <section>
                <h5 className={theme.placeholderSectionLabel}>
                    Placeholders{onInsert && ' — click to insert at the cursor'}
                </h5>
                <table>
                    <tbody>
                        {(kind === 'layout' ? LAYOUT_ROWS : CONTENT_ROWS).map((row) => (
                            <tr key={row.placeholders[0].label}>
                                <td>
                                    {row.placeholders.map(({ label, insert }) =>
                                        onInsert ? (
                                            <button
                                                type="button"
                                                key={label}
                                                className={theme.placeholderInsert}
                                                title="Insert at cursor"
                                                onClick={() => onInsert(insert ?? label)}>
                                                <code>{label}</code>
                                            </button>
                                        ) : (
                                            <code key={label} className={theme.placeholderCode}>
                                                {label}
                                            </code>
                                        )
                                    )}
                                </td>
                                <td>{row.note}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        </div>
    </DropdownButton>
);

export default PlaceholderReference;
